// Angular imports
import { Injectable, OnDestroy, inject } from '@angular/core';

// Project libraries
import { MembersRole, PermissionsService } from '@tgx/shared/enums';
import { AdminMemberProfile, MemberInfo } from '@tgx/shared/interfaces';
import { AdminService, PlatformInfoService, UserManagementService, UserProfileService } from '@tgx/shared/services';
import { ToastsService } from '@tgx/shared/toasts';
import { Subject, takeUntil } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class OnboardingMemeberManagementService implements OnDestroy {
  private platformInfoService = inject(PlatformInfoService);
  private userManagementService = inject(UserManagementService);
  private adminService = inject(AdminService);
  private userProfileService = inject(UserProfileService);
  private toast = inject(ToastsService);

  private selectedCRMID: string;
  private selectedOrgCode: string;
  private loggedUserEmail = '';
  private loggedUserRole: MembersRole | null = null;
  private destroy$ = new Subject<void>();

  constructor() {
    this.platformInfoService.userLogged$.pipe(takeUntil(this.destroy$)).subscribe((user) => {
      this.loggedUserEmail = user?.email?.toLowerCase?.() ?? '';
      this.loggedUserRole = user?.role ?? null;
    });

    this.platformInfoService.organizationSelected$.pipe(takeUntil(this.destroy$)).subscribe({
      next: (org) => {
        if (org && org.crmID) {
          this.selectedCRMID = org.crmID;
          this.selectedOrgCode = org.code;
        }
      },
      error: (err) => {
        console.log(err);
      },
    });
  }

  async onAddMember(contact: MemberInfo): Promise<any> {
    const email = contact.email;

    // Owner contact must never be reassigned in IAM, but profile data (like job title) still needs updates.
    if (this.isLoggedOwnerContact(email)) {
      try {
        const memberProfile = await this.userProfileService.getAdminMemberProfileByEmail(email);
        await this.updateMemberAdminData(
          email,
          {
            ...contact,
            role: this.loggedUserRole ?? MembersRole.OWNER,
          },
          memberProfile,
        );
      } catch (error) {
        this.toast.handleGatewayAndGraphqlErrors(error);
        throw error;
      }
      return null;
    }

    try {
      const user: MemberInfo = {
        email,
        firstName: contact.firstName,
        lastName: contact.lastName,
        jobTitle: contact.jobTitle,
        role: MembersRole.VIEWER,
        newRoleInHotelX: contact.newRoleInHotelX,
        newRoleInEntities: contact.newRoleInEntities,
        newRoleInBilling: contact.newRoleInBilling,
        newRoleInUser: contact.newRoleInUser,
        newRoleInFastX: contact.newRoleInFastX,
      };

      const response = await this.userManagementService.addMember(
        this.selectedOrgCode,
        this.selectedCRMID,
        user,
        this.buildUserCreationInput(user),
        this.buildUserUpdateInput(contact),
      );

      const memberCode = response?.[0] || email;
      await this.processServiceRoles(memberCode, user);

      return response;
    } catch (error: any) {
      this.toast.addToast(`Error send invitation to email ${email}`, 'bg-danger', error?.message ?? error);
      throw error;
    }
  }

  private async processServiceRoles(email: string, currentData: MemberInfo): Promise<void> {
    const roleMappings: { prop: keyof MemberInfo; service: PermissionsService }[] = [
      { prop: 'newRoleInHotelX', service: PermissionsService.HOTELX },
      { prop: 'newRoleInEntities', service: PermissionsService.ENTITIES },
      { prop: 'newRoleInBilling', service: PermissionsService.BILLING },
      { prop: 'newRoleInUser', service: PermissionsService.USER },
      { prop: 'newRoleInFastX', service: PermissionsService.FASTX },
    ];

    // In create mode we also need to persist VIEWER roles explicitly.
    const tasks = roleMappings.filter(
      (mapping) => currentData[mapping.prop] !== undefined && currentData[mapping.prop] !== null,
    );

    if (tasks.length === 0) {
      return;
    }

    const roles = tasks.map((task) => currentData[task.prop] as MembersRole);
    const minimumRole = this.getMinimumRole(roles);

    // Keep org role aligned with the minimum role selected across service permissions.
    await this.adminService.updateMemberDataInOrganization(email, this.selectedOrgCode, {
      role: minimumRole,
    });

    const failedServices: PermissionsService[] = [];
    for (const task of tasks) {
      try {
        await this.adminService.updateMemberServiceRole(
          email,
          this.selectedOrgCode,
          currentData[task.prop] as MembersRole,
          task.service,
        );
      } catch {
        // Already logged by GqlService; failures are reported together in the warning toast below.
        failedServices.push(task.service);
      }
    }

    if (failedServices.length > 0) {
      this.toast.addToast(
        'Warning',
        'bg-warning',
        `Some service roles could not be updated: ${failedServices.join(', ')}`,
      );
    }
  }

  private getMinimumRole(roles: MembersRole[]): MembersRole {
    const hierarchy: MembersRole[] = [
      MembersRole.GUEST,
      MembersRole.VIEWER,
      MembersRole.EDITOR,
      MembersRole.ADMIN,
      MembersRole.OWNER,
    ];

    return roles.reduce((minimumRole, role) => {
      const minimumRoleIndex = hierarchy.indexOf(minimumRole);
      const roleIndex = hierarchy.indexOf(role);
      return roleIndex < minimumRoleIndex ? role : minimumRole;
    }, roles[0]);
  }

  private async updateMemberAdminData(
    email: string,
    data: MemberInfo,
    memberProfile?: AdminMemberProfile | null,
  ): Promise<void> {
    if (!email || !this.selectedOrgCode) return;

    const updates: Promise<string>[] = [];
    const orgProfile = memberProfile?.organizations?.find(
      (organization) => organization.organization?.code === this.selectedOrgCode,
    );
    const firstNameChanged = this.hasTextChanged(data.firstName, memberProfile?.firstName);
    const lastNameChanged = this.hasTextChanged(data.lastName, memberProfile?.lastName);
    const hasNameChanges = firstNameChanged || lastNameChanged;
    const hasJobTitleChange = this.hasTextChanged(data.jobTitle, orgProfile?.jobTitle);

    if (hasNameChanges) {
      const firstName = this.normalizeText(data.firstName ?? memberProfile?.firstName ?? '');
      const lastName = this.normalizeText(data.lastName ?? memberProfile?.lastName ?? '');
      updates.push(this.adminService.updateMemberProfile(email, firstName, lastName));
    }

    if (hasJobTitleChange) {
      updates.push(
        this.adminService.updateMemberDataInOrganization(email, this.selectedOrgCode, {
          jobTitle: this.normalizeText(data.jobTitle),
        }),
      );
    }

    if (updates.length > 0) {
      await Promise.all(updates);
    }
  }

  private hasTextChanged(nextValue?: string, currentValue?: string): boolean {
    if (nextValue === undefined) {
      return false;
    }

    return this.normalizeText(nextValue) !== this.normalizeText(currentValue);
  }

  private normalizeText(value?: string): string {
    return value?.trim() ?? '';
  }

  private buildUserCreationInput(user: MemberInfo) {
    return {
      email: user.email,
      firstName: user.firstName,
      lastName: user.lastName,
      partnerIncludedInInput: {
        orgCode: this.selectedOrgCode,
        jobTitle: user.jobTitle,
      },
    };
  }

  private buildUserUpdateInput(contact: MemberInfo) {
    return {
      add: [
        {
          orgCode: this.selectedOrgCode,
          jobTitle: contact.jobTitle,
        },
      ],
    };
  }

  private isLoggedOwnerContact(email: string): boolean {
    const normalizedEmail = email?.trim().toLowerCase();
    const normalizedLoggedEmail = this.loggedUserEmail?.trim().toLowerCase();
    const normalizedRole = String(this.loggedUserRole ?? '').toUpperCase();

    return !!normalizedEmail && normalizedEmail === normalizedLoggedEmail && normalizedRole === MembersRole.OWNER;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
